import { supabase as defaultClient } from '../lib/supabase';

const convertToSnakeCase = (obj) => ({
  title:           obj?.title,
  content:         obj?.content,
  // ✅ Accept both camelCase (from formData) and snake_case (from handleStatusChange)
  file_url:        obj?.fileUrl        ?? obj?.file_url        ?? null,
  file_type:       obj?.fileType       ?? obj?.file_type       ?? null,
  cta_button_text: obj?.ctaButtonText  ?? obj?.cta_button_text ?? null,
  cta_button_url:  obj?.ctaButtonUrl   ?? obj?.cta_button_url  ?? null,
  status:          obj?.status,
  created_by:      obj?.createdBy      ?? obj?.created_by      ?? null,
  published_at:    obj?.publishedAt    ?? obj?.published_at    ?? null,
});

const convertToCamelCase = (obj) => {
  if (!obj) return null;
  return {
    id:            obj?.id,
    title:         obj?.title,
    content:       obj?.content,
    fileUrl:       obj?.file_url,
    fileType:      obj?.file_type,
    ctaButtonText: obj?.cta_button_text,
    ctaButtonUrl:  obj?.cta_button_url,
    status:        obj?.status,
    createdBy:     obj?.created_by,
    createdAt:     obj?.created_at,
    updatedAt:     obj?.updated_at,
    publishedAt:   obj?.published_at,
  };
};

export const announcementService = {

  // Get all announcements (admin view)
  async getAllAnnouncements(client = defaultClient) {
    try {
      const { data, error } = await (client || defaultClient)
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data?.map(convertToCamelCase) || [];
    } catch (error) {
      console.error('Error fetching announcements:', error);
      throw error;
    }
  },

  // Get published announcements (customer/banner view)
  async getPublishedAnnouncements(client = defaultClient) {
    try {
      const { data, error } = await (client || defaultClient)
        .from('announcements')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      return data?.map(convertToCamelCase) || [];
    } catch (error) {
      console.error('Error fetching published announcements:', error);
      throw error;
    }
  },

  // Create announcement
  async createAnnouncement(announcementData, client = defaultClient) {
    try {
      const { data, error } = await (client || defaultClient)
        .from('announcements')
        .insert([convertToSnakeCase(announcementData)])
        .select();

      if (error) throw error;
      return convertToCamelCase(data?.[0]);
    } catch (error) {
      console.error('Error creating announcement:', error);
      throw error;
    }
  },

  // Update announcement — no .single() to avoid "coerce" error
  async updateAnnouncement(id, announcementData, client = defaultClient) {
    try {
      const payload = convertToSnakeCase(announcementData);
      // ✅ Always stamp updated_at
      payload.updated_at = new Date().toISOString();
      // ✅ Drop keys that are undefined so we don't null out untouched fields
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      const { data, error } = await (client || defaultClient)
        .from('announcements')
        .update(payload)
        .eq('id', id)
        .select();

      if (error) throw error;
      return convertToCamelCase(data?.[0]);
    } catch (error) {
      console.error('Error updating announcement:', error);
      throw error;
    }
  },

  // Delete announcement
  async deleteAnnouncement(id, client = defaultClient) {
    try {
      const { error } = await (client || defaultClient)
        .from('announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting announcement:', error);
      throw error;
    }
  },

  // Upload announcement file
  async uploadAnnouncementFile(file, client = defaultClient) {
    try {
      const timestamp     = Date.now();
      const sanitizedName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath      = `${timestamp}-${sanitizedName}`;

      const { data, error } = await (client || defaultClient)
        .storage
        .from('announcement-files')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (error) throw error;

      const { data: urlData } = (client || defaultClient)
        .storage
        .from('announcement-files')
        .getPublicUrl(data?.path);

      return { path: data?.path, url: urlData?.publicUrl };
    } catch (error) {
      console.error('Error uploading announcement file:', error);
      throw error;
    }
  },

  // Delete announcement file
  async deleteAnnouncementFile(filePath, client = defaultClient) {
    try {
      const { error } = await (client || defaultClient)
        .storage
        .from('announcement-files')
        .remove([filePath]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting announcement file:', error);
      throw error;
    }
  },
};